import React from 'react';
import { Box, Fade, Typography } from '@mui/material';
import InfoTip from '../../../../../components/ui/InfoTip';
import MySkeleton from '../../../../../components/Skeleton';
import { STATUS_TOOLTIP_TEXT_SX } from '../utils/programTypes';

const SKELETON_HEIGHT = 168;

interface Props {
  title: string;
  tooltip?: string;
  loading?: boolean;
  isEmpty?: boolean;
  emptyText?: string;
  skeletonCount?: number;
  variant?: 'admin' | 'user';
  children?: React.ReactNode;
}

const containerSx = {
  flex: 1,
  minWidth: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
} as const;

const ProgramColumn: React.FC<Props> = ({
  title,
  tooltip,
  loading = false,
  isEmpty = false,
  emptyText,
  skeletonCount = 3,
  variant = 'admin',
  children
}) => {
  const wrapperSx =
    variant === 'admin'
      ? {
          ...containerSx,
          background: '#1C1C1C',
          px: '10px',
          borderRight: '1px solid #2A2A2A'
        }
      : {
          ...containerSx,
          background: '#1C1C1C',
          borderRadius: 2,
          border: '1px solid #2A2A2A'
        };

  const headerSx =
    variant === 'admin'
      ? { py: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }
      : {
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        };

  return (
    <Box sx={wrapperSx}>
      <Box sx={headerSx}>
        <Typography
          sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: variant === 'admin' ? 500 : 400, fontFamily: 'Inter' }}
        >
          {title}
        </Typography>
        {tooltip ? (
          <InfoTip title={<Typography sx={STATUS_TOOLTIP_TEXT_SX}>{tooltip}</Typography>} placement="bottom-end" />
        ) : (
          <Box sx={{ width: 16, height: 16, border: '1.5px solid #868686', borderRadius: 0.5 }} />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pr: variant === 'admin' ? 1 : 0,
          p: variant === 'user' ? 1.25 : undefined,
          display: 'flex',
          flexDirection: 'column',
          gap: variant === 'user' ? 1.25 : 0,
          minHeight: 0,
          position: 'relative'
        }}
      >
        {loading ? (
          <Fade in timeout={150}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {Array.from({ length: skeletonCount }).map((_, idx) => (
                <MySkeleton key={idx} variant="rounded" height={SKELETON_HEIGHT} />
              ))}
            </Box>
          </Fade>
        ) : isEmpty ? (
          emptyText ? (
            <Typography sx={{ color: '#B6B6B6', mt: 4, textAlign: 'center', fontSize: 12, fontFamily: 'Inter' }}>
              {emptyText}
            </Typography>
          ) : null
        ) : (
          <Fade in timeout={250}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: variant === 'user' ? 1.25 : 1.25 }}>
              {children}
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(ProgramColumn);
